import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Divider,
  MenuItem,
  Modal, 
  TextField, 
  Typography
} from '@mui/material';

import AmountTextField from '../../../../components/textFields/AmountTextField';
import useCurrencyService from '../../../../components/hooks/useCurrencyService';
import LoadingButton from '../../../../components/buttons/Button';

import { 
  CreateSubscriptionPlanBIZsubs, 
  UpdateSubscriptionPlanBIZsubs 
} from '../../../../redux/actions/business/subscriptions/subscriptionPlansActions';
import { 
  GetTenantsBIZidentity 
} from '../../../../redux/actions/business/identity/tenantsActions';

import global from "../../../../global.scss";

const Data = {
  PlanName: "",
  MonthlyFee: 0,
  YearlyFee: 0,
  Currency: "USD",
  NumOfFreeUsers: 0,
  ExtraUserMonthlyFee: 0,
  FreeAchMonthlyTransfers: 0,
  Tenant: "",
};

const SubscriptionPlansModal = ({
  open, 
  setOpen, 
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const { handleEditAmount, handleCalculateAmount } = useCurrencyService();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(1);

  const [tenantsList, setTenantsList] = useState([]);

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(1);
  }

  useEffect(() => {
    try {
      dispatch(GetTenantsBIZidentity(1, 1000, "", setLoading));
      if(open){
        if(update.defaultMode === 1){
          // mode = 1 - create subscription plan
          setMode(1);
        }else if(update.defaultMode === 2){
          // mode = 2 - edit subscription plan
          setMode(2);
          setFormData({
            ...formData,
            PlanName: update.plan_name || "",
            MonthlyFee: handleEditAmount(update.currency, update.monthly_fee || 0),
            YearlyFee: handleEditAmount(update.currency, update.yearly_fee || 0),
            Currency: update.currency || "USD",
            NumOfFreeUsers: update.num_of_free_users || 0,
            ExtraUserMonthlyFee: handleEditAmount(update.currency, update.extra_user_monthly_fee || 0),
            FreeAchMonthlyTransfers: update.free_ach_monthly_transfers || 0,
            Tenant: update.ref_tenant_id || "",
          })
        }else{
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    try {
      setTenantsList(state.tenantsList.records || []);
    } catch (err) {}
  }, [state]);

  // --------------- handle change ----------------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  const handleSubmit = () => {
    if(mode === 1){
      // create subscription plan
      let obj = {
        plan_name: formData.PlanName || "",
        monthly_fee: handleCalculateAmount(formData.Currency, formData.MonthlyFee || 0),
        yearly_fee: handleCalculateAmount(formData.Currency, formData.YearlyFee || 0),
        currency: formData.Currency || "",
        num_of_free_users: formData.NumOfFreeUsers || 0,
        extra_user_monthly_fee: handleCalculateAmount(formData.Currency, formData.ExtraUserMonthlyFee || 0),
        free_ach_monthly_transfers: formData.FreeAchMonthlyTransfers || 0,
        ref_tenant_id: formData.Tenant || "",
      };
      dispatch(CreateSubscriptionPlanBIZsubs(obj, setLoading)).then(() => {
        handleGet().then(() => {
          handleClose();
        })
      });
    }else if(mode === 2){
      // update subscription plan
      let obj = {
        plan_name: formData.PlanName || "",
        monthly_fee: handleCalculateAmount(formData.Currency, formData.MonthlyFee || 0),
        yearly_fee: handleCalculateAmount(formData.Currency, formData.YearlyFee || 0),
        currency: formData.Currency || "",
        num_of_free_users: formData.NumOfFreeUsers || 0,
        extra_user_monthly_fee: handleCalculateAmount(formData.Currency, formData.ExtraUserMonthlyFee || 0),
        free_ach_monthly_transfers: formData.FreeAchMonthlyTransfers || 0,
        ref_tenant_id: formData.Tenant || "",
      };
      dispatch(UpdateSubscriptionPlanBIZsubs(update.id, obj, setLoading)).then(() => {
        handleGet().then(() => {
          handleClose();
        })
      });
    }else{
      toast.error("Invalid modal");
      handleClose();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"}
            sx={{ padding: "15px"}}>
            <Typography variant='h6' sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                select
                label="Tenant"
                fullWidth
                name="Tenant"
                value={formData.Tenant}
                onChange={handleChange}
                inputProps={{ maxLength: 255 }}>
                {
                  tenantsList.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.id}>
                      {item.name}&nbsp;
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {item.id}
                      </Typography>
                    </MenuItem>
                  ))
                }
              </TextField>
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                label="Plan name"
                fullWidth
                name="PlanName"
                value={formData.PlanName}
                onChange={handleChange}
                inputProps={{ maxLength: 255 }}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <AmountTextField 
                currencyName="Currency"
                currency={formData.Currency}
                onCurrencyChange={handleChange}
                label="Monthly fee"
                fullWidth
                name="MonthlyFee"
                value={formData.MonthlyFee}
                onChange={handleChange}
                />
              <AmountTextField 
                currencyName="Currency"
                currency={formData.Currency}
                onCurrencyChange={handleChange}
                label="Yearly fee"
                fullWidth
                name="YearlyFee"
                value={formData.YearlyFee}
                onChange={handleChange}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                label="Number of free users"
                fullWidth
                name="NumOfFreeUsers"
                type='number'
                value={formData.NumOfFreeUsers}
                onChange={handleChange}
                />
              <AmountTextField 
                currencyName="Currency"
                currency={formData.Currency}
                onCurrencyChange={handleChange}
                label="Extra user monthly fee"
                fullWidth
                name="ExtraUserMonthlyFee"
                value={formData.ExtraUserMonthlyFee}
                onChange={handleChange}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                label="Free ach monthly transfers"
                name="FreeAchMonthlyTransfers"
                type='number'
                value={formData.FreeAchMonthlyTransfers}
                onChange={handleChange}
                />
            </Box>
          </Box>
          <Divider
           sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px"}}>
            <Button variant='text'
              onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton 
              variant='contained'
              loading={loading}
              disabled={!(formData.PlanName !== "" && formData.Tenant !== "")}
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default SubscriptionPlansModal;